import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
  notification,
  Select,
  Button,
  Divider
} from "antd";
import moment from "moment";
import {
  HiOutlineClock,
  HiOutlineSave,
  HiOutlineChevronLeft,
  HiOutlineInformationCircle,
  HiOutlineCalendar
} from 'react-icons/hi';
import "./WorkingHoursMaster.css";

const { Option } = Select;

/* ── Compact Time input component ─────────────────────────── */
const TimeInput = ({ label, value, onChange, placeholder }) => {
  return (
    <div>
      <label className="block text-[11px] font-bold text-gray-700 mb-0.5 uppercase tracking-wider">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-teal-600 pointer-events-none">
          <HiOutlineClock size={15} />
        </span>
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-8 pr-2 py-0.5 text-gray-800 font-semibold text-xs border border-gray-200 rounded-md
                     bg-white focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-100
                     hover:border-teal-300 transition-all cursor-pointer shadow-2xs"
          style={{ height: '32px' }}
        />
      </div>
      {value ? (
        <p className="mt-0.5 text-[10px] text-teal-700 font-semibold px-0.5 m-0">
          {moment(value, 'HH:mm').format('hh:mm A')}
        </p>
      ) : (
        <p className="mt-0.5 text-[10px] text-gray-400 font-medium px-0.5 m-0">
          Select time
        </p>
      )}
    </div>
  );
};

const WorkingHoursComponent = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Data states
  const [services, setServices] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);

  // Form states — times stored as "HH:mm" strings
  const [selectedService, setSelectedService] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const days = [
    { label: 'Sun', value: 1 },
    { label: 'Mon', value: 2 },
    { label: 'Tue', value: 3 },
    { label: 'Wed', value: 4 },
    { label: 'Thu', value: 5 },
    { label: 'Fri', value: 6 },
    { label: 'Sat', value: 7 },
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const doctorId = Cookies.get("doctor_id") || Cookies.get("Doctor_id") || localStorage.getItem("Doctor_id");

      const [servicesRes, typesRes] = await Promise.all([
        axiosInstance.post("doctor/getDoctorService", { doctor_id: doctorId }),
        axiosInstance.get("doctor/get_mas_appointment_type")
      ]);

      if (servicesRes.data?.status === 1) setServices(servicesRes.data.data || []);
      if (typesRes.data?.status === 1) setAppointmentTypes(typesRes.data.data || []);
    } catch (err) {
      console.error("Failed to load initial data", err);
      notification.error({ message: "Failed to load services or appointment types" });
    } finally {
      setLoading(false);
    }
  };

  // Calculate total slots for summary
  const calcSlots = () => {
    if (!startTime || !endTime || !selectedService?.slot_duration) return 0;
    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    if (!end.isAfter(start)) return 0;

    const durationString = String(selectedService.slot_duration);
    let durationInMinutes = 0;
    if (durationString.includes(':')) {
      const parts = durationString.split(':').map(Number);
      durationInMinutes = (parts[0] * 60) + parts[1];
    } else {
      durationInMinutes = parseInt(durationString);
    }
    if (durationInMinutes <= 0) return 0;

    return Math.floor(end.diff(start, 'minutes') / durationInMinutes);
  };

  const handleSave = async () => {
    if (!selectedService || !selectedType || selectedDays.length === 0 || !startTime || !endTime) {
      notification.warning({ message: "Please fill all required fields" });
      return;
    }

    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');

    if (!start.isBefore(end)) {
      notification.warning({ message: "End time must be after start time" });
      return;
    }

    const durationString = String(selectedService.slot_duration);
    let durationInMinutes = 0;
    if (durationString.includes(':')) {
      const parts = durationString.split(':').map(Number);
      durationInMinutes = (parts[0] * 60) + parts[1];
    } else {
      durationInMinutes = parseInt(durationString);
    }

    const noOfSlots = Math.floor(end.diff(start, 'minutes') / durationInMinutes);

    if (noOfSlots <= 0) {
      notification.error({ message: "Invalid time range for the selected service duration" });
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        clinicId: Cookies.get("clinic_id") || Cookies.get("Clinic_id") || 0,
        doctorId: Cookies.get("doctor_id") || Cookies.get("Doctor_id") || localStorage.getItem("Doctor_id"),
        serviceId: selectedService.id,
        fromtime: `${startTime}:00`,
        totime: `${endTime}:00`,
        slotduration: selectedService.slot_duration,
        NoOfslots: noOfSlots,
        appointmentType: selectedType.id,
        createdby: Cookies.get("doctor_id") || Cookies.get("Doctor_id") || localStorage.getItem("Doctor_id"),
        ipaddress: "127.0.0.1",
        days: selectedDays,
        specialityId: selectedService.specid || selectedService.speciality_id || selectedService.specialityId || Cookies.get("speciality_Id") || 0
      };

      const response = await axiosInstance.post("doctor/insertdocAppointmentSettings", payload);

      if (response.data?.status === 1 || response.data?.ErrorCode === "9999") {
        notification.success({
          message: "Success!",
          description: "Working hours added successfully."
        });
        history.push("/dashboard/workinghours");
      } else {
        notification.error({ message: response.data?.msg || "Failed to save schedule" });
      }
    } catch (err) {
      console.error("Save failed", err);
      notification.error({ message: "An error occurred while saving" });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="w-full space-y-2.5 animate-in fade-in duration-300 text-gray-800">
      {/* Header Bar - Compact Full Width */}
      <div className="bg-white px-4 py-2.5 rounded-lg border border-gray-200 shadow-2xs flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => history.goBack()}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 transition-colors cursor-pointer"
            title="Go Back"
          >
            <HiOutlineChevronLeft size={18} />
          </button>
          <div>
            <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">Add Working Hours</h2>
            <p className="text-gray-400 text-[11px] m-0 font-medium">Define your availability and recurring slot schedules</p>
          </div>
        </div>
        <button
          onClick={() => history.push("/dashboard/workinghours")}
          className="px-3 py-1 rounded font-bold text-xs text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 transition-all cursor-pointer"
        >
          View Existing Schedule
        </button>
      </div>

      {/* Main Content Layout - Full Width 3-Column Compact Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        {/* Left 2 Columns: Primary Form */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs space-y-3">
            <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-wider m-0 pb-1.5 border-b border-gray-100 flex items-center gap-1.5">
              <HiOutlineCalendar className="text-teal-600" size={15} />
              Schedule Details
            </h3>

            {/* Service & Appointment Type Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-0.5">
                  Medical Service <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  style={{ height: '32px' }}
                  placeholder="Select medical service"
                  loading={loading}
                  value={selectedService?.id}
                  onChange={(val) => setSelectedService(services.find(s => s.id === val))}
                >
                  {services.map(s => (
                    <Option key={s.id} value={s.id}>
                      <div className="flex justify-between items-center w-full text-xs">
                        <span className="font-semibold">{s.service}</span>
                        <span className="text-[10px] text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded font-bold">
                          {s.slot_duration}m
                        </span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-0.5">
                  Appointment Type <span className="text-red-500">*</span>
                </label>
                <Select
                  className="w-full"
                  style={{ height: '32px' }}
                  placeholder="Select appointment type"
                  loading={loading}
                  value={selectedType?.id}
                  onChange={(val) => setSelectedType(appointmentTypes.find(t => t.id === val))}
                >
                  {appointmentTypes.map(t => (
                    <Option key={t.id} value={t.id}>{t.appointment_type}</Option>
                  ))}
                </Select>
              </div>
            </div>

            <Divider className="my-1.5" />

            {/* Time Slot Range */}
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-700 m-0">Time Range</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TimeInput
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  placeholder="Select Start Time"
                />
                <TimeInput
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  placeholder="Select End Time"
                />
              </div>

              {/* Working window preview callout */}
              {startTime && endTime && (
                <div className="bg-teal-50/80 border border-teal-200 rounded-md px-3 py-1.5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <HiOutlineClock className="text-teal-600 shrink-0" size={16} />
                    <span className="text-xs text-teal-900 font-bold">
                      Working Window: {moment(startTime, 'HH:mm').format('hh:mm A')} → {moment(endTime, 'HH:mm').format('hh:mm A')}
                    </span>
                  </div>
                  {calcSlots() > 0 && (
                    <span className="text-[11px] font-black text-teal-700 bg-teal-100/80 px-2 py-0.5 rounded">
                      {calcSlots()} Slots ({selectedService?.slot_duration} min/slot)
                    </span>
                  )}
                </div>
              )}
            </div>

            <Divider className="my-1.5" />

            {/* Repeat On (Days Selection) */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-xs font-bold text-gray-700 m-0">
                  Repeat On (Working Days) <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  className="text-xs font-bold text-teal-600 hover:text-teal-800 transition-colors cursor-pointer bg-transparent border-none p-0"
                  onClick={() => setSelectedDays(selectedDays.length === 7 ? [] : [1, 2, 3, 4, 5, 6, 7])}
                >
                  {selectedDays.length === 7 ? 'Deselect All' : 'Select All Days'}
                </button>
              </div>

              <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
                {days.map(day => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => {
                        const next = isSelected
                          ? selectedDays.filter(d => d !== day.value)
                          : [...selectedDays, day.value];
                        setSelectedDays(next);
                      }}
                      className={`py-1.5 px-1 rounded-md text-xs font-bold transition-all border cursor-pointer flex items-center justify-center ${
                        isSelected
                          ? 'bg-teal-600 border-teal-600 text-white shadow-2xs'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-teal-400 hover:bg-teal-50/40'
                      }`}
                    >
                      <span>{day.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right 1 Column: Schedule Overview Summary */}
        <div className="space-y-3">
          <div className="bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs space-y-3 sticky top-16">
            <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider m-0 pb-1.5 border-b border-gray-100">
              Schedule Overview
            </h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Service</span>
                <span className="font-bold text-gray-800 text-right max-w-[65%] truncate">
                  {selectedService?.service || '--'}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Appointment Type</span>
                <span className="font-bold text-gray-800">
                  {selectedType?.appointment_type || '--'}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Working Hours</span>
                <span className="font-bold text-gray-800 text-right">
                  {startTime && endTime
                    ? `${moment(startTime, 'HH:mm').format('hh:mm A')} - ${moment(endTime, 'HH:mm').format('hh:mm A')}`
                    : '--'}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Slot Duration</span>
                <span className="font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-100">
                  {selectedService ? `${selectedService.slot_duration} mins` : '--'}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">Active Days</span>
                <span className="font-bold text-gray-800">
                  {selectedDays.length > 0 ? `${selectedDays.length} days / week` : '--'}
                </span>
              </div>

              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider m-0">Total Daily Slots</p>
                  <p className="text-[10px] text-gray-400 m-0">Per active day</p>
                </div>
                <span className="text-xl font-black text-teal-600 bg-teal-50 px-2.5 py-0.5 rounded-lg border border-teal-100">
                  {calcSlots()}
                </span>
              </div>
            </div>

            <div className="bg-blue-50/70 border border-blue-100 p-2 rounded-md flex items-start gap-2">
              <HiOutlineInformationCircle className="text-blue-500 shrink-0 mt-0.5" size={15} />
              <p className="text-[11px] text-blue-800 font-medium leading-tight m-0">
                Saving creates recurring booking slots for selected days.
              </p>
            </div>

            <Button
              type="primary"
              size="middle"
              block
              className="h-8 rounded-md bg-[#009688] hover:bg-[#00897b] border-none font-bold text-xs shadow-2xs cursor-pointer flex items-center justify-center gap-1.5"
              loading={saveLoading}
              onClick={handleSave}
              icon={<HiOutlineSave size={15} />}
            >
              Save Schedule
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursComponent;
